
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, UserPlus, Users, RefreshCw } from "lucide-react";
import { useUnassignedUsers } from '@/hooks/useUserAssignment';
import { AssignReferralDialog } from './AssignReferralDialog';
import { BulkAssignDialog } from './BulkAssignDialog';
import { DataConsistencyPanel } from './DataConsistencyPanel';
import { UnassignedUser } from '@/services/userAssignmentService';
import { formatDate } from '@/utils/formatters';
import { useIsMobile } from '@/hooks/use-mobile';

export const UnassignedUsersTable = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [assignDialogUser, setAssignDialogUser] = useState<UnassignedUser | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  
  const { data: users, isLoading, refetch } = useUnassignedUsers();

  const filteredUsers = users ? users.filter(user => 
    user.masked_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.masked_email.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  const selectedUsersData = filteredUsers.filter(user => selectedUsers.includes(user.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleAssignUser = (user: UnassignedUser) => {
    setAssignDialogUser(user);
    setAssignDialogOpen(true);
  };

  const handleBulkAssign = () => {
    setBulkAssignDialogOpen(true);
  };

  const handleBulkAssignComplete = () => {
    setSelectedUsers([]);
    refetch();
  };

  const handleRefresh = () => {
    refetch();
    setSelectedUsers([]);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Data Consistency Panel */}
      <DataConsistencyPanel />

      {/* Unassigned Users Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Unassigned Users ({filteredUsers.length})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="ml-2"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {selectedUsers.length > 0 && (
              <Button className="w-full md:w-auto" onClick={handleBulkAssign}>
                <UserPlus className="h-4 w-4 mr-2" />
                Bulk Assign ({selectedUsers.length})
              </Button>
            )}
          </div>

          {filteredUsers.length > 0 ? (
            isMobile ? (
              // Mobile card layout
              <div className="space-y-3">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                        />
                        <div>
                          <div className="font-medium">{user.masked_name}</div>
                          <div className="text-sm text-muted-foreground">{user.masked_email}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Joined: {formatDate(new Date(user.created_at))}
                      </div>
                      <Button size="sm" onClick={() => handleAssignUser(user)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assign
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop table layout
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Join Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUsers.includes(user.id)}
                            onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                          />
                        </TableCell>
                        <TableCell>{user.masked_name}</TableCell>
                        <TableCell>{user.masked_email}</TableCell>
                        <TableCell>{formatDate(new Date(user.created_at))}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" onClick={() => handleAssignUser(user)}>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Assign
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              {searchQuery ? 'No users found matching your search' : 'No unassigned users found'}
            </div>
          )}
        </CardContent>
      </Card>

      <AssignReferralDialog
        user={assignDialogUser}
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
      />

      <BulkAssignDialog
        users={selectedUsersData}
        open={bulkAssignDialogOpen}
        onOpenChange={setBulkAssignDialogOpen}
        onComplete={handleBulkAssignComplete}
      />
    </div>
  );
};
